import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { 
  FileText, 
  Download, 
  Plus, 
  Trash2, 
  Upload, 
  Eye,
  Save,
  Camera,
  ClipboardCheck,
  Building,
  User,
  Euro,
  AlertTriangle,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Schadensarten
const DAMAGE_TYPES = [
  { id: 'wasserschaden', label: 'Wasserschaden', icon: '💧' },
  { id: 'brandschaden', label: 'Brandschaden', icon: '🔥' },
  { id: 'schimmelbefall', label: 'Schimmelbefall', icon: '🦠' },
  { id: 'sturmschaden', label: 'Sturmschaden', icon: '🌪️' },
  { id: 'vandalismus', label: 'Vandalismus', icon: '🔨' },
  { id: 'frostschaden', label: 'Frostschaden', icon: '❄️' },
  { id: 'rohrbruch', label: 'Rohrbruch', icon: '🚿' },
  { id: 'andere', label: 'Andere', icon: '📋' }
];

// Betroffene Räume
const ROOMS = [
  { id: 'wohnzimmer', label: 'Wohnzimmer' },
  { id: 'schlafzimmer', label: 'Schlafzimmer' },
  { id: 'kinderzimmer', label: 'Kinderzimmer' },
  { id: 'kueche', label: 'Küche' },
  { id: 'bad', label: 'Bad/WC' },
  { id: 'flur', label: 'Flur/Diele' },
  { id: 'keller', label: 'Keller' },
  { id: 'dachboden', label: 'Dachboden' },
  { id: 'garage', label: 'Garage' },
  { id: 'balkon', label: 'Balkon/Terrasse' },
  { id: 'fassade', label: 'Fassade/Außen' },
  { id: 'treppenhaus', label: 'Treppenhaus' }
];

// Empfohlene Maßnahmen
const RECOMMENDED_ACTIONS = [
  { id: 'trocknung', label: 'Technische Trocknung' },
  { id: 'schimmelbeseitigung', label: 'Schimmelbeseitigung' },
  { id: 'malerarbeiten', label: 'Malerarbeiten' },
  { id: 'tapezieren', label: 'Tapezierarbeiten' },
  { id: 'putzarbeiten', label: 'Putzarbeiten' },
  { id: 'bodenbelag', label: 'Bodenbelag erneuern' },
  { id: 'decke', label: 'Deckenarbeiten' },
  { id: 'daemmung', label: 'Dämmungsarbeiten' },
  { id: 'sanierung', label: 'Komplettsanierung' },
  { id: 'reinigung', label: 'Spezialreinigung' }
];

// Collapsible Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-[#9B1B30]" />}
          <span className="font-semibold text-gray-800 text-base sm:text-lg">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

const AdminGutachten = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [savedGutachten, setSavedGutachten] = useState([]);
  const [activeTab, setActiveTab] = useState('neu');
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [aiLoading, setAiLoading] = useState({});  // Track AI loading state for each field
  const [imageDescription, setImageDescription] = useState('');  // Description for AI image analysis
  const [showImageDescDialog, setShowImageDescDialog] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  
  // Customer search states
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    gutachtenNumber: '',
    clientName: '',
    clientStreet: '',
    clientCity: '',
    clientPhone: '',
    clientEmail: '',
    objectStreet: '',
    objectCity: '',
    objectType: 'wohnung',
    objectOwner: '',
    insuranceCompany: '',
    insuranceNumber: '',
    claimNumber: '',
    inspectionDate: new Date().toISOString().split('T')[0],
    inspectionTime: '10:00',
    damageTypes: [],
    otherDamageType: '',
    affectedRooms: [],
    damageDescription: '',
    damageCause: '',
    damageExtent: 'mittel',
    recommendedActions: [],
    additionalNotes: '',
    costItems: [{ id: 1, description: '', quantity: 1, unit: 'Stk', price: 0, total: 0 }],
    photos: []
  });

  // AI Text Enhancement Function
  const enhanceTextWithAI = async (fieldName, fieldType) => {
    const text = formData[fieldName];
    if (!text || text.trim().length < 5) {
      toast.error('Bitte geben Sie mindestens 5 Zeichen ein, bevor Sie die KI-Verbesserung nutzen');
      return;
    }

    setAiLoading(prev => ({ ...prev, [fieldName]: true }));
    try {
      const response = await api.post('/ai/enhance-text', {
        text: text,
        field_type: fieldType
      });

      if (response.data.enhanced_text) {
        setFormData(prev => ({
          ...prev,
          [fieldName]: response.data.enhanced_text
        }));
        toast.success('Text wurde durch KI verbessert');
      }
    } catch (error) {
      console.error('AI enhancement error:', error);
      toast.error(error.response?.data?.detail || 'KI-Verbesserung fehlgeschlagen');
    } finally {
      setAiLoading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // AI Cost Items Generation Function
  const generateCostItemsWithAI = async () => {
    if (formData.damageTypes.length === 0 && formData.affectedRooms.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Schadensart oder einen Raum aus');
      return;
    }

    setAiLoading(prev => ({ ...prev, costItems: true }));
    try {
      const response = await api.post('/ai/generate-cost-items', {
        damageTypes: formData.damageTypes,
        affectedRooms: formData.affectedRooms,
        damageExtent: formData.damageExtent,
        recommendedActions: formData.recommendedActions,
        damageDescription: formData.damageDescription,
        damageCause: formData.damageCause
      });

      if (response.data.cost_items && response.data.cost_items.length > 0) {
        setFormData(prev => ({
          ...prev,
          costItems: response.data.cost_items
        }));
        toast.success(`${response.data.cost_items.length} Kostenpositionen wurden generiert`);
      }
    } catch (error) {
      console.error('AI cost generation error:', error);
      toast.error(error.response?.data?.detail || 'KI-Kostenberechnung fehlgeschlagen');
    } finally {
      setAiLoading(prev => ({ ...prev, costItems: false }));
    }
  };

  // AI Image Analysis Function
  const analyzeImageWithAI = async (imageBase64, description = '') => {
    setAiLoading(prev => ({ ...prev, imageAnalysis: true }));
    try {
      // Remove data URL prefix if present
      let base64Data = imageBase64;
      if (imageBase64.includes(',')) {
        base64Data = imageBase64.split(',')[1];
      }
      
      const response = await api.post('/ai/analyze-damage-image', {
        image_base64: base64Data,
        image_type: 'image/jpeg',
        user_description: description
      });

      if (response.data.success && response.data.analysis) {
        const analysis = response.data.analysis;
        
        // Update form with AI analysis results
        setFormData(prev => {
          const updates = { ...prev };
          
          // Set damage types
          if (analysis.damage_type) {
            const damageTypeMap = {
              'wasserschaden': 'wasserschaden',
              'brandschaden': 'brandschaden',
              'schimmelbefall': 'schimmelbefall',
              'sturmschaden': 'sturmschaden',
              'frostschaden': 'frostschaden',
              'vandalismus': 'vandalismus'
            };
            const mappedType = damageTypeMap[analysis.damage_type.toLowerCase()];
            if (mappedType && !prev.damageTypes.includes(mappedType)) {
              updates.damageTypes = [...prev.damageTypes, mappedType];
            } else if (!mappedType && analysis.damage_type !== 'andere') {
              updates.damageTypes = [...prev.damageTypes, 'andere'];
              updates.otherDamageType = analysis.damage_type;
            }
          }
          
          // Set damage description
          if (analysis.damage_description) {
            updates.damageDescription = analysis.damage_description;
          }
          
          // Set damage cause
          if (analysis.damage_cause) {
            updates.damageCause = analysis.damage_cause;
          }
          
          // Set damage extent
          if (analysis.damage_severity) {
            const severityMap = {
              'gering': 'gering',
              'mittel': 'mittel',
              'schwer': 'schwer',
              'sehr_schwer': 'sehr_schwer'
            };
            updates.damageExtent = severityMap[analysis.damage_severity] || 'mittel';
          }
          
          // Set affected rooms
          if (analysis.affected_rooms && Array.isArray(analysis.affected_rooms)) {
            const roomMap = {
              'wohnzimmer': 'wohnzimmer', 'schlafzimmer': 'schlafzimmer',
              'kinderzimmer': 'kinderzimmer', 'küche': 'kueche', 'kueche': 'kueche',
              'bad': 'bad', 'badezimmer': 'bad', 'wc': 'bad',
              'flur': 'flur', 'diele': 'flur', 'keller': 'keller',
              'dachboden': 'dachboden', 'garage': 'garage',
              'balkon': 'balkon', 'terrasse': 'balkon',
              'fassade': 'fassade', 'außen': 'fassade',
              'treppenhaus': 'treppenhaus'
            };
            const mappedRooms = analysis.affected_rooms
              .map(r => roomMap[r.toLowerCase()] || null)
              .filter(Boolean);
            updates.affectedRooms = [...new Set([...prev.affectedRooms, ...mappedRooms])];
          }
          
          // Set recommended actions
          if (analysis.recommended_actions && Array.isArray(analysis.recommended_actions)) {
            const actionMap = {
              'trocknung': 'trocknung', 'technische trocknung': 'trocknung',
              'schimmelbeseitigung': 'schimmelbeseitigung', 'schimmel': 'schimmelbeseitigung',
              'malerarbeiten': 'malerarbeiten', 'streichen': 'malerarbeiten',
              'tapezieren': 'tapezieren', 'tapete': 'tapezieren',
              'putzarbeiten': 'putzarbeiten', 'putz': 'putzarbeiten',
              'bodenbelag': 'bodenbelag', 'boden': 'bodenbelag',
              'decke': 'decke', 'deckenarbeiten': 'decke',
              'dämmung': 'daemmung', 'dämmungsarbeiten': 'daemmung',
              'sanierung': 'sanierung', 'komplettsanierung': 'sanierung',
              'reinigung': 'reinigung', 'spezialreinigung': 'reinigung'
            };
            const mappedActions = analysis.recommended_actions
              .map(a => actionMap[a.toLowerCase()] || null)
              .filter(Boolean);
            updates.recommendedActions = [...new Set([...prev.recommendedActions, ...mappedActions])];
          }
          
          // Set cost items
          if (analysis.cost_items && Array.isArray(analysis.cost_items)) {
            updates.costItems = analysis.cost_items.map((item, idx) => ({
              id: idx + 1,
              description: item.description || '',
              quantity: item.quantity || 1,
              unit: item.unit || 'Stk',
              price: item.price || 0,
              total: item.total || (item.quantity * item.price) || 0
            }));
          }
          
          // Set additional notes
          if (analysis.additional_notes) {
            updates.additionalNotes = analysis.additional_notes;
          }
          
          return updates;
        });
        
        toast.success('Bild wurde analysiert und Gutachten ausgefüllt!');
      }
    } catch (error) {
      console.error('AI image analysis error:', error);
      toast.error(error.response?.data?.detail || 'Bildanalyse fehlgeschlagen');
    } finally {
      setAiLoading(prev => ({ ...prev, imageAnalysis: false }));
    }
  };

  // Handle photo upload with AI analysis option
  const handlePhotoUploadWithAnalysis = async (e, analyzeWithAI = false) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const file = files[0];
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      const base64Data = event.target.result;
      
      if (analyzeWithAI) {
        // Store the image and open description dialog
        setPendingImageFile(base64Data);
        setImageDescription('');
        setShowImageDescDialog(true);
      } else {
        // Just add photo without analysis
        const newPhoto = {
          id: Date.now() + Math.random(),
          data: base64Data,
          caption: ''
        };
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, newPhoto]
        }));
      }
    };
    reader.readAsDataURL(file);
    
    // Reset the input
    e.target.value = '';
  };
  
  // Confirm image analysis with description
  const confirmImageAnalysis = async () => {
    if (!pendingImageFile) return;
    
    // Add photo to list
    const newPhoto = {
      id: Date.now() + Math.random(),
      data: pendingImageFile,
      caption: imageDescription
    };
    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, newPhoto]
    }));
    
    // Close dialog
    setShowImageDescDialog(false);
    
    // Analyze with description
    await analyzeImageWithAI(pendingImageFile, imageDescription);
    
    // Clear pending
    setPendingImageFile(null);
    setImageDescription('');
  };

  // Load customers from API
  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  // Filtered customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const search = customerSearch.toLowerCase();
    return customers.filter(c => 
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.customer_number?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone?.toLowerCase().includes(search) ||
      c.city?.toLowerCase().includes(search)
    );
  }, [customers, customerSearch]);

  // Select customer and fill form data
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      clientName: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
      clientPhone: customer.phone || '',
      clientEmail: customer.email || '',
      clientStreet: customer.street || '',
      clientCity: customer.city || ''
    }));
    setCustomerSearch('');
    toast.success(`Kunde "${customer.first_name} ${customer.last_name}" ausgewählt`);
  };

  // Auto-save to localStorage to prevent data loss
  const AUTO_SAVE_KEY = 'gutachten_autosave';
  
  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (formData.clientName || formData.objectStreet || formData.damageDescription || formData.photos?.length > 0) {
      const dataToSave = {
        ...formData,
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(dataToSave));
    }
  }, [formData]);

  // Load auto-saved data on mount
  useEffect(() => {
    const savedData = localStorage.getItem(AUTO_SAVE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const lastSaved = new Date(parsed.lastSaved);
        const hoursSinceSave = (Date.now() - lastSaved.getTime()) / (1000 * 60 * 60);
        
        // Only restore if saved within last 24 hours
        if (hoursSinceSave < 24 && (parsed.clientName || parsed.objectStreet || parsed.photos?.length > 0)) {
          toast.info(
            <div>
              <p className="font-medium">Nicht gespeicherte Daten gefunden!</p>
              <p className="text-sm">Vom {lastSaved.toLocaleDateString('de-DE')} um {lastSaved.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'})}</p>
              <div className="flex gap-2 mt-2">
                <button 
                  onClick={() => {
                    setFormData(prev => ({...prev, ...parsed, lastSaved: undefined}));
                    toast.success('Daten wiederhergestellt');
                  }}
                  className="bg-green-500 text-white px-3 py-1 rounded text-sm"
                >
                  Wiederherstellen
                </button>
                <button 
                  onClick={() => {
                    localStorage.removeItem(AUTO_SAVE_KEY);
                    toast.info('Auto-Save gelöscht');
                  }}
                  className="bg-gray-500 text-white px-3 py-1 rounded text-sm"
                >
                  Verwerfen
                </button>
              </div>
            </div>,
            { duration: 15000 }
          );
        }
      } catch (e) {
        console.error('Error parsing auto-save:', e);
      }
    }
    
    // Check if we have data from document conversion
    const conversionData = localStorage.getItem('gutachten_from_document');
    if (conversionData) {
      try {
        const parsed = JSON.parse(conversionData);
        localStorage.removeItem('gutachten_from_document');
        setFormData(prev => ({
          ...prev,
          gutachtenNumber: parsed.gutachtenNumber || prev.gutachtenNumber,
          clientName: parsed.clientName || '',
          clientStreet: parsed.clientStreet || '',
          clientCity: parsed.clientCity || '',
          clientPhone: parsed.clientPhone || '',
          objectStreet: parsed.objectStreet || '',
          objectCity: parsed.objectCity || '',
          objectOwner: parsed.objectOwner || '',
          objectType: parsed.objectType || 'wohnung',
          inspectionDate: parsed.inspectionDate || prev.inspectionDate,
          inspectionTime: parsed.inspectionTime || '10:00',
          damageTypes: parsed.damageTypes || [],
          otherDamageType: parsed.otherDamageType || '',
          affectedRooms: parsed.affectedRooms || [],
          damageExtent: parsed.damageExtent || 'mittel',
          damageCause: parsed.damageCause || '',
          damageDescription: parsed.damageDescription || '',
          recommendedActions: parsed.recommendedActions || [],
          additionalNotes: parsed.additionalNotes || '',
          costItems: parsed.costItems?.length > 0 
            ? parsed.costItems.map((item, idx) => ({ ...item, id: idx + 1 }))
            : prev.costItems,
          insuranceCompany: parsed.insuranceCompany || '',
          insuranceNumber: parsed.insuranceNumber || '',
          claimNumber: parsed.claimNumber || '',
        }));
        setActiveTab('neu');
        toast.success(
          `Gutachten aus ${parsed.source_document_type === 'rechnung' ? 'Rechnung' : parsed.source_document_type === 'angebot' ? 'Angebot' : 'Dokument'} ${parsed.source_document_number || ''} erstellt. Bitte überprüfen und anpassen.`,
          { duration: 8000 }
        );
        // Skip loading auto-save data since we have conversion data
        loadSavedGutachten();
        loadCustomers();
        return;
      } catch (e) {
        console.error('Error loading conversion data:', e);
        localStorage.removeItem('gutachten_from_document');
      }
    }
    
    loadSavedGutachten();
    generateGutachtenNumber();
    loadCustomers();
  }, []);

  // Clear auto-save when successfully saved
  const clearAutoSave = () => {
    localStorage.removeItem(AUTO_SAVE_KEY);
  };

  const generateGutachtenNumber = async () => {
    try {
      const response = await api.get('/admin/gutachten/next-number');
      setFormData(prev => ({ ...prev, gutachtenNumber: response.data.number }));
    } catch (error) {
      const year = new Date().getFullYear();
      const num = String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0');
      setFormData(prev => ({ ...prev, gutachtenNumber: `GA-${year}-${num}` }));
    }
  };

  const loadSavedGutachten = async () => {
    try {
      const response = await api.get('/admin/gutachten');
      setSavedGutachten(response.data || []);
    } catch (error) {
      console.error('Error loading gutachten:', error);
    }
  };

  const handleDamageTypeChange = (typeId, checked) => {
    setFormData(prev => ({
      ...prev,
      damageTypes: checked 
        ? [...prev.damageTypes, typeId]
        : prev.damageTypes.filter(t => t !== typeId)
    }));
  };

  const handleRoomChange = (roomId, checked) => {
    setFormData(prev => ({
      ...prev,
      affectedRooms: checked
        ? [...prev.affectedRooms, roomId]
        : prev.affectedRooms.filter(r => r !== roomId)
    }));
  };

  const handleActionChange = (actionId, checked) => {
    setFormData(prev => ({
      ...prev,
      recommendedActions: checked
        ? [...prev.recommendedActions, actionId]
        : prev.recommendedActions.filter(a => a !== actionId)
    }));
  };

  const addCostItem = () => {
    const newId = Math.max(...formData.costItems.map(i => i.id), 0) + 1;
    setFormData(prev => ({
      ...prev,
      costItems: [...prev.costItems, { id: newId, description: '', quantity: 1, unit: 'Stk', price: 0, total: 0 }]
    }));
  };

  const updateCostItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      costItems: prev.costItems.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'price') {
            updated.total = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.price) || 0);
          }
          return updated;
        }
        return item;
      })
    }));
  };

  const removeCostItem = (id) => {
    if (formData.costItems.length > 1) {
      setFormData(prev => ({
        ...prev,
        costItems: prev.costItems.filter(item => item.id !== id)
      }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newPhotos = [];
    for (const file of files) {
      const reader = new FileReader();
      const photoData = await new Promise((resolve) => {
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
      newPhotos.push({
        id: Date.now() + Math.random(),
        name: file.name,
        data: photoData,
        caption: ''
      });
    }

    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos]
    }));
    toast.success(`${files.length} Foto(s) hinzugefügt`);
  };

  const removePhoto = (photoId) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const updatePhotoCaption = (photoId, caption) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.map(p => p.id === photoId ? { ...p, caption } : p)
    }));
  };

  const calculateTotal = () => {
    return formData.costItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const generatePDF = async (download = false, save = false) => {
    if (!formData.gutachtenNumber || !formData.objectStreet) {
      toast.error('Bitte füllen Sie mindestens die Gutachten-Nr. und Adresse aus');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/admin/gutachten/generate-pdf', {
        ...formData,
        save_document: save
      });

      if (download) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
        link.download = `Gutachten_${formData.gutachtenNumber}.pdf`;
        link.click();
        toast.success('PDF heruntergeladen');
      } else {
        setPreviewPdf(`data:application/pdf;base64,${response.data.pdf_base64}`);
        setPreviewOpen(true);
      }

      if (save) {
        toast.success('Gutachten gespeichert und im Archiv abgelegt');
        clearAutoSave();  // Clear auto-save after successful save
        loadSavedGutachten();
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      const errorDetail = error.response?.data?.detail || '';
      
      if (errorDetail.includes('CSRF')) {
        toast.error('Sitzung abgelaufen - bitte Seite neu laden und erneut versuchen');
      } else if (error.response?.status === 401) {
        toast.error('Nicht angemeldet - bitte erneut einloggen');
      } else {
        toast.error(`Fehler: ${errorDetail || 'PDF-Generierung fehlgeschlagen'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      gutachtenNumber: '',
      clientName: '',
      clientStreet: '',
      clientCity: '',
      clientPhone: '',
      clientEmail: '',
      objectStreet: '',
      objectCity: '',
      objectType: 'wohnung',
      objectOwner: '',
      insuranceCompany: '',
      insuranceNumber: '',
      claimNumber: '',
      inspectionDate: new Date().toISOString().split('T')[0],
      inspectionTime: '10:00',
      damageTypes: [],
      otherDamageType: '',
      affectedRooms: [],
      damageDescription: '',
      damageCause: '',
      damageExtent: 'mittel',
      recommendedActions: [],
      additionalNotes: '',
      costItems: [{ id: 1, description: '', quantity: 1, unit: 'Stk', price: 0, total: 0 }],
      photos: []
    });
    generateGutachtenNumber();
  };

  const loadGutachten = async (id) => {
    try {
      const response = await api.get(`/admin/gutachten/${id}`);
      setFormData(response.data);
      setActiveTab('neu');
      toast.success('Gutachten geladen');
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
  };

  const downloadSavedGutachten = async (id) => {
    try {
      const response = await api.get(`/admin/gutachten/${id}/pdf`);
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
      link.download = `Gutachten_${response.data.number}.pdf`;
      link.click();
    } catch (error) {
      toast.error('Fehler beim Download');
    }
  };

  // Input styling for this page - Clean professional look
  const inputClass = "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-[#9B1B30] focus:ring-[#9B1B30]/20";
  const labelClass = "text-gray-700 text-sm font-medium";

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto touch-pan-y">
      {/* Header with brand color */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-[#9B1B30] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <ClipboardCheck className="w-8 h-8" />
                Schadensgutachten
              </h1>
              <p className="text-white/80 mt-1 text-sm sm:text-base">Professionelle Gutachten für Versicherungen</p>
            </div>
            
            {/* Tab Buttons */}
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={() => setActiveTab('neu')}
                className={`flex-1 sm:flex-none ${activeTab === 'neu' 
                  ? 'bg-white text-[#9B1B30] hover:bg-white/90' 
                  : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'}`}
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Neues </span>Gutachten
            </Button>
            <Button
              onClick={() => setActiveTab('archiv')}
              className={`flex-1 sm:flex-none ${activeTab === 'archiv' 
                ? 'bg-white text-[#9B1B30] hover:bg-white/90' 
                : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'}`}
            >
              <FileText className="w-4 h-4 mr-2" />
              Archiv ({savedGutachten.length})
            </Button>
          </div>
        </div>
        </div>
      </div>

      {activeTab === 'neu' ? (
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Main Form Column */}
            <div className="lg:col-span-2 space-y-4">
              
              {/* Gutachten-Nummer & Besichtigung */}
              <Section title="Gutachten-Daten" icon={FileText}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className={labelClass}>Gutachten-Nr.</Label>
                    <Input
                      value={formData.gutachtenNumber}
                      onChange={(e) => setFormData({...formData, gutachtenNumber: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                      placeholder="GA-2026-0001"
                    />
                  </div>
                  <div>
                    <Label className={labelClass}>Datum</Label>
                    <Input
                      type="date"
                      value={formData.inspectionDate}
                      onChange={(e) => setFormData({...formData, inspectionDate: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                    />
                  </div>
                  <div>
                    <Label className={labelClass}>Uhrzeit</Label>
                    <Input
                      type="time"
                      value={formData.inspectionTime}
                      onChange={(e) => setFormData({...formData, inspectionTime: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label className={labelClass}>Objektart</Label>
                    <Select value={formData.objectType} onValueChange={(v) => setFormData({...formData, objectType: v})}>
                      <SelectTrigger className={`mt-1 ${inputClass}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wohnung">Wohnung</SelectItem>
                        <SelectItem value="haus">Einfamilienhaus</SelectItem>
                        <SelectItem value="mehrfamilienhaus">Mehrfamilienhaus</SelectItem>
                        <SelectItem value="gewerbe">Gewerbe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Section>

              {/* Auftraggeber */}
              <Section title="Auftraggeber" icon={User}>
                <div className="mt-4 space-y-4">
                  {/* Customer Search */}
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <Label className={`${labelClass} mb-2 block`}>Kunde suchen</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Name, Kundennr., E-Mail, Telefon, Ort..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className={`pl-10 ${inputClass}`}
                        data-testid="gutachten-customer-search"
                      />
                    </div>
                    <Select onValueChange={(val) => {
                      const customer = customers.find(c => c.id === val);
                      if (customer) selectCustomer(customer);
                    }}>
                      <SelectTrigger className={`mt-2 ${inputClass}`}>
                        <SelectValue placeholder="Kunde aus Liste wählen..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[250px]">
                        {filteredCustomers.length === 0 ? (
                          <div className="p-3 text-center text-gray-500 text-sm">
                            {customerSearch ? 'Keine Kunden gefunden' : 'Keine Kunden vorhanden'}
                          </div>
                        ) : (
                          filteredCustomers.map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{c.customer_number} - {c.first_name} {c.last_name}</span>
                                <span className="text-xs text-gray-500">{c.city} {c.email && `• ${c.email}`}</span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {selectedCustomer && (
                      <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                        <span>✓</span>
                        <span className="truncate">{selectedCustomer.first_name} {selectedCustomer.last_name} ausgewählt</span>
                      </p>
                    )}
                  </div>
                  
                  {/* Manual Entry Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className={labelClass}>Name</Label>
                      <Input
                        value={formData.clientName}
                        onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                        className={`mt-1 ${inputClass}`}
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div>
                      <Label className={labelClass}>Telefon</Label>
                      <Input
                        value={formData.clientPhone}
                        onChange={(e) => setFormData({...formData, clientPhone: e.target.value})}
                        className={`mt-1 ${inputClass}`}
                        placeholder="0123 456789"
                      />
                    </div>
                    <div>
                      <Label className={labelClass}>E-Mail</Label>
                      <Input
                        type="email"
                        value={formData.clientEmail}
                        onChange={(e) => setFormData({...formData, clientEmail: e.target.value})}
                        className={`mt-1 ${inputClass}`}
                        placeholder="kunde@email.de"
                      />
                    </div>
                    <div>
                      <Label className={labelClass}>PLZ / Ort</Label>
                      <Input
                        value={formData.clientCity}
                        onChange={(e) => setFormData({...formData, clientCity: e.target.value})}
                        className={`mt-1 ${inputClass}`}
                        placeholder="12345 Musterstadt"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className={labelClass}>Straße</Label>
                      <AddressAutocomplete
                        value={formData.clientStreet}
                        onChange={(val) => setFormData({...formData, clientStreet: val})}
                        onAddressSelect={(addr) => setFormData({
                          ...formData,
                          clientStreet: addr.street,
                          clientCity: addr.postcode && addr.city ? `${addr.postcode} ${addr.city}` : formData.clientCity
                        })}
                        placeholder="Straße eingeben..."
                        className={`mt-1 [&_input]:bg-white [&_input]:border-gray-300 [&_input]:text-gray-900 [&_input]:placeholder-gray-400`}
                      />
                    </div>
                  </div>
                </div>
              </Section>

              {/* Schadensobjekt */}
              <Section title="Schadensobjekt" icon={Building}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  <div>
                    <Label className={labelClass}>Straße *</Label>
                    <AddressAutocomplete
                      value={formData.objectStreet}
                      onChange={(val) => setFormData({...formData, objectStreet: val})}
                      onAddressSelect={(addr) => setFormData({
                        ...formData,
                        objectStreet: addr.street,
                        objectCity: addr.postcode && addr.city ? `${addr.postcode} ${addr.city}` : formData.objectCity
                      })}
                      placeholder="Straße eingeben..."
                      className={`mt-1 [&_input]:bg-white [&_input]:border-gray-300 [&_input]:text-gray-900 [&_input]:placeholder-gray-400`}
                      required
                    />
                  </div>
                  <div>
                    <Label className={labelClass}>PLZ / Ort *</Label>
                    <Input
                      value={formData.objectCity}
                      onChange={(e) => setFormData({...formData, objectCity: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                      placeholder="12345 Musterstadt"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className={labelClass}>Eigentümer (falls abweichend)</Label>
                    <Input
                      value={formData.objectOwner}
                      onChange={(e) => setFormData({...formData, objectOwner: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                      placeholder="Name des Eigentümers"
                    />
                  </div>
                </div>
              </Section>

              {/* Versicherung */}
              <Section title="Versicherungsdaten" icon={AlertTriangle} defaultOpen={false}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                  <div>
                    <Label className={labelClass}>Versicherung</Label>
                    <Input
                      value={formData.insuranceCompany}
                      onChange={(e) => setFormData({...formData, insuranceCompany: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                      placeholder="z.B. Allianz"
                    />
                  </div>
                  <div>
                    <Label className={labelClass}>Versicherungsnr.</Label>
                    <Input
                      value={formData.insuranceNumber}
                      onChange={(e) => setFormData({...formData, insuranceNumber: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                      placeholder="VS-123456"
                    />
                  </div>
                  <div>
                    <Label className={labelClass}>Schadensnr.</Label>
                    <Input
                      value={formData.claimNumber}
                      onChange={(e) => setFormData({...formData, claimNumber: e.target.value})}
                      className={`mt-1 ${inputClass}`}
                      placeholder="SN-2026-001"
                    />
                  </div>
                </div>
              </Section>

              {/* Schadensarten */}
              <Section title="Schadensart(en)" icon={AlertTriangle}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4">
                  {DAMAGE_TYPES.map(type => (
                    <label
                      key={type.id}
                      className={`flex items-center gap-2 p-2 sm:p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.damageTypes.includes(type.id)
                          ? 'bg-[#9B1B30] text-white border-[#9B1B30]'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Checkbox
                        checked={formData.damageTypes.includes(type.id)}
                        onCheckedChange={(checked) => handleDamageTypeChange(type.id, checked)}
                        className="border-current"
                      />
                      <span className="text-lg">{type.icon}</span>
                      <span className="text-xs sm:text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
                {formData.damageTypes.includes('andere') && (
                  <div className="mt-3">
                    <div className="flex gap-2">
                      <Input
                        value={formData.otherDamageType}
                        onChange={(e) => setFormData({...formData, otherDamageType: e.target.value})}
                        className={`${inputClass} flex-1`}
                        placeholder="Andere Schadensart beschreiben..."
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => enhanceTextWithAI('otherDamageType', 'other_damage_type')}
                        disabled={aiLoading.otherDamageType || !formData.otherDamageType}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shrink-0"
                        title="Mit KI verbessern"
                      >
                        {aiLoading.otherDamageType ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </Section>

              {/* Betroffene Räume */}
              <Section title="Betroffene Räume" icon={Building}>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-4">
                  {ROOMS.map(room => (
                    <label
                      key={room.id}
                      className={`flex items-center gap-1 sm:gap-2 p-2 rounded border cursor-pointer transition-all text-xs sm:text-sm ${
                        formData.affectedRooms.includes(room.id)
                          ? 'bg-[#9B1B30] text-white border-[#9B1B30]'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Checkbox
                        checked={formData.affectedRooms.includes(room.id)}
                        onCheckedChange={(checked) => handleRoomChange(room.id, checked)}
                        className="border-current w-3 h-3 sm:w-4 sm:h-4"
                      />
                      <span className="truncate">{room.label}</span>
                    </label>
                  ))}
                </div>
              </Section>

              {/* Schadensbeschreibung */}
              <Section title="Schadensbeschreibung" icon={FileText}>
                <div className="space-y-4 mt-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className={labelClass}>Schadensursache</Label>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => enhanceTextWithAI('damageCause', 'damage_cause')}
                        disabled={aiLoading.damageCause || !formData.damageCause}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs h-7 px-2"
                        title="Mit KI verbessern"
                      >
                        {aiLoading.damageCause ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Sparkles className="w-3 h-3 mr-1" />
                        )}
                        KI
                      </Button>
                    </div>
                    <Textarea
                      value={formData.damageCause}
                      onChange={(e) => setFormData({...formData, damageCause: e.target.value})}
                      className={`${inputClass} min-h-[80px]`}
                      placeholder="Beschreiben Sie die Ursache des Schadens..."
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className={labelClass}>Detaillierte Beschreibung</Label>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => enhanceTextWithAI('damageDescription', 'damage_description')}
                        disabled={aiLoading.damageDescription || !formData.damageDescription}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs h-7 px-2"
                        title="Mit KI verbessern"
                      >
                        {aiLoading.damageDescription ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Sparkles className="w-3 h-3 mr-1" />
                        )}
                        KI
                      </Button>
                    </div>
                    <Textarea
                      value={formData.damageDescription}
                      onChange={(e) => setFormData({...formData, damageDescription: e.target.value})}
                      className={`${inputClass} min-h-[120px]`}
                      placeholder="Beschreiben Sie den Schaden im Detail..."
                    />
                  </div>
                  <div>
                    <Label className={labelClass}>Schadensausmaß</Label>
                    <Select value={formData.damageExtent} onValueChange={(v) => setFormData({...formData, damageExtent: v})}>
                      <SelectTrigger className={`mt-1 ${inputClass} w-full sm:w-48`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gering">Gering</SelectItem>
                        <SelectItem value="mittel">Mittel</SelectItem>
                        <SelectItem value="schwer">Schwer</SelectItem>
                        <SelectItem value="sehr_schwer">Sehr schwer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Section>

              {/* Empfohlene Maßnahmen */}
              <Section title="Empfohlene Maßnahmen" icon={ClipboardCheck}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-4">
                  {RECOMMENDED_ACTIONS.map(action => (
                    <label
                      key={action.id}
                      className={`flex items-center gap-1 sm:gap-2 p-2 rounded border cursor-pointer transition-all text-xs sm:text-sm ${
                        formData.recommendedActions.includes(action.id)
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <Checkbox
                        checked={formData.recommendedActions.includes(action.id)}
                        onCheckedChange={(checked) => handleActionChange(action.id, checked)}
                        className="border-current w-3 h-3 sm:w-4 sm:h-4"
                      />
                      <span className="truncate">{action.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <Label className={labelClass}>Zusätzliche Hinweise</Label>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => enhanceTextWithAI('additionalNotes', 'additional_notes')}
                      disabled={aiLoading.additionalNotes || !formData.additionalNotes}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs h-7 px-2"
                      title="Mit KI verbessern"
                    >
                      {aiLoading.additionalNotes ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      KI
                    </Button>
                  </div>
                  <Textarea
                    value={formData.additionalNotes}
                    onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
                    className={`${inputClass} min-h-[80px]`}
                    placeholder="Weitere Hinweise oder Empfehlungen..."
                  />
                </div>
              </Section>

              {/* Kostenaufstellung */}
              <Section title="Kostenaufstellung" icon={Euro}>
                <div className="mt-4 space-y-3">
                  {/* AI Generate Button */}
                  <div className="flex justify-center sm:justify-end mb-2">
                    <Button
                      type="button"
                      onClick={generateCostItemsWithAI}
                      disabled={aiLoading.costItems || (formData.damageTypes.length === 0 && formData.affectedRooms.length === 0)}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white w-full sm:w-auto text-sm sm:text-base"
                      title="KI generiert Kostenpositionen basierend auf Ihren Angaben"
                    >
                      {aiLoading.costItems ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      <span className="hidden sm:inline">KI-Kostenaufstellung generieren</span>
                      <span className="sm:hidden">KI-Kosten generieren</span>
                    </Button>
                  </div>
                  
                  {/* Mobile: Card-based layout */}
                  <div className="sm:hidden space-y-3">
                    {formData.costItems.map((item, idx) => (
                      <div key={item.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs text-gray-500">Position {idx + 1}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeCostItem(item.id)}
                            disabled={formData.costItems.length === 1}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-6 w-6 -mt-1 -mr-1"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <Input
                          value={item.description}
                          onChange={(e) => updateCostItem(item.id, 'description', e.target.value)}
                          className={`${inputClass} mb-2`}
                          placeholder="Beschreibung"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-gray-500">Menge</Label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateCostItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                              className={`${inputClass} text-sm`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Einheit</Label>
                            <Select value={item.unit} onValueChange={(v) => updateCostItem(item.id, 'unit', v)}>
                              <SelectTrigger className={`${inputClass} text-sm`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Stk">Stk</SelectItem>
                                <SelectItem value="m²">m²</SelectItem>
                                <SelectItem value="lfm">lfm</SelectItem>
                                <SelectItem value="Std">Std</SelectItem>
                                <SelectItem value="psch">psch</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Preis €</Label>
                            <Input
                              type="number"
                              value={item.price}
                              onChange={(e) => updateCostItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                              className={`${inputClass} text-sm`}
                            />
                          </div>
                        </div>
                        <div className="mt-2 text-right">
                          <span className="text-[#9B1B30] font-bold">{item.total.toFixed(2)} €</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Desktop: Grid layout */}
                  <div className="hidden sm:block space-y-2">
                    {formData.costItems.map((item, idx) => (
                      <div key={item.id} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-5">
                          {idx === 0 && <Label className={`${labelClass} text-xs`}>Beschreibung</Label>}
                          <Input
                            value={item.description}
                            onChange={(e) => updateCostItem(item.id, 'description', e.target.value)}
                            className={inputClass}
                            placeholder="z.B. Malerarbeiten"
                          />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <Label className={`${labelClass} text-xs`}>Menge</Label>}
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateCostItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                            className={inputClass}
                          />
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <Label className={`${labelClass} text-xs`}>Einh.</Label>}
                          <Select value={item.unit} onValueChange={(v) => updateCostItem(item.id, 'unit', v)}>
                            <SelectTrigger className={inputClass}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Stk">Stk</SelectItem>
                              <SelectItem value="m²">m²</SelectItem>
                              <SelectItem value="lfm">lfm</SelectItem>
                              <SelectItem value="Std">Std</SelectItem>
                              <SelectItem value="psch">psch</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <Label className={`${labelClass} text-xs`}>Preis €</Label>}
                          <Input
                            type="number"
                            value={item.price}
                            onChange={(e) => updateCostItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                            className={inputClass}
                          />
                        </div>
                        <div className="col-span-2 flex items-center gap-2">
                          <span className="text-gray-900 font-medium text-sm">{item.total.toFixed(2)}€</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => removeCostItem(item.id)}
                            disabled={formData.costItems.length === 1}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Button onClick={addCostItem} className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 w-full sm:w-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    Position hinzufügen
                  </Button>
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <div className="text-right">
                      <span className="text-gray-500 text-xs sm:text-sm">Gesamtkosten (netto):</span>
                      <span className="text-xl sm:text-3xl font-bold text-[#9B1B30] ml-2 sm:ml-3">
                        {calculateTotal().toFixed(2)} €
                      </span>
                    </div>
                  </div>
                </div>
              </Section>

              {/* Fotos - Mobile */}
              <Section title={`Fotos (${formData.photos.length})`} icon={Camera} defaultOpen={false}>
                <div className="mt-4 space-y-4">
                  {/* AI Analysis Upload */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-dashed border-purple-300 rounded-xl p-4">
                    <div className="text-center">
                      <Sparkles className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                      <h4 className="font-semibold text-purple-700 mb-1">KI-Schadenanalyse</h4>
                      <p className="text-sm text-purple-600 mb-3">
                        Laden Sie ein Foto hoch - die KI analysiert den Schaden und füllt das Gutachten automatisch aus
                      </p>
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all">
                        {aiLoading.imageAnalysis ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Analysiere...</span>
                          </>
                        ) : (
                          <>
                            <Camera className="w-5 h-5" />
                            <span>Foto hochladen & analysieren</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={(e) => handlePhotoUploadWithAnalysis(e, true)}
                          disabled={aiLoading.imageAnalysis}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                  
                  {/* Normal Upload */}
                  <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#9B1B30] transition-colors bg-gray-50">
                    <Upload className="w-5 h-5 text-gray-400 mb-1" />
                    <span className="text-sm text-gray-500">Weitere Fotos hinzufügen (ohne KI-Analyse)</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handlePhotoUploadWithAnalysis(e, false)}
                      className="hidden"
                    />
                  </label>

                  {formData.photos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {formData.photos.map((photo, idx) => (
                        <div key={photo.id} className="relative bg-gray-100 rounded-lg p-2">
                          <img
                            src={photo.data}
                            alt={`Foto ${idx + 1}`}
                            className="w-full h-24 object-cover rounded"
                          />
                          <div className="absolute top-2 right-2 flex gap-1">
                            {/* Analyze this photo button */}
                            <Button
                              size="icon"
                              variant="secondary"
                              className="w-6 h-6 bg-purple-500 hover:bg-purple-600 text-white"
                              onClick={() => {
                                setPendingImageFile(photo.data);
                                setImageDescription(photo.caption || '');
                                setShowImageDescDialog(true);
                              }}
                              disabled={aiLoading.imageAnalysis}
                              title="Mit KI analysieren"
                            >
                              {aiLoading.imageAnalysis ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Sparkles className="w-3 h-3" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="destructive"
                              className="w-6 h-6"
                              onClick={() => removePhoto(photo.id)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                          <Input
                            value={photo.caption}
                            onChange={(e) => updatePhotoCaption(photo.id, e.target.value)}
                            className={`mt-2 ${inputClass} text-xs`}
                            placeholder="Beschreibung..."
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            </div>

            {/* Summary Card - Sidebar on large screens */}
            <div className="hidden lg:block">
              <div className="sticky top-4">
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                  <h3 className="font-semibold text-gray-800 mb-4">Zusammenfassung</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Schadensarten:</span>
                      <span className="font-medium text-gray-900">{formData.damageTypes.length}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Betroffene Räume:</span>
                      <span className="font-medium text-gray-900">{formData.affectedRooms.length}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Maßnahmen:</span>
                      <span className="font-medium text-gray-900">{formData.recommendedActions.length}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Fotos:</span>
                      <span className="font-medium text-gray-900">{formData.photos.length}</span>
                    </div>
                    <hr className="border-gray-200 my-3" />
                    <div className="flex justify-between text-lg">
                      <span className="text-gray-600">Gesamt:</span>
                      <span className="font-bold text-[#9B1B30]">{calculateTotal().toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions - Fixed at bottom */}
          <div className="mt-6 bg-white rounded-xl p-3 sm:p-6 border border-gray-200 shadow-sm">
            {/* Summary for mobile - responsive grid */}
            <div className="lg:hidden grid grid-cols-3 gap-2 mb-4 text-center">
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-xs text-gray-500">Schäden</div>
                <div className="font-bold text-gray-900">{formData.damageTypes.length}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <div className="text-xs text-gray-500">Räume</div>
                <div className="font-bold text-gray-900">{formData.affectedRooms.length}</div>
              </div>
              <div className="bg-[#9B1B30]/10 rounded-lg p-2">
                <div className="text-xs text-[#9B1B30]">Gesamt</div>
                <div className="font-bold text-[#9B1B30]">{calculateTotal().toFixed(2)}€</div>
              </div>
            </div>
            
            {/* Action Buttons - 2x2 grid on mobile, row on desktop */}
            <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3">
              <Button
                className="bg-[#9B1B30] text-white hover:bg-[#7d1526] h-11 sm:h-10 text-sm sm:text-base"
                onClick={() => generatePDF(false, false)}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-1 sm:mr-2" />}
                <span className="hidden xs:inline">Vorschau</span>
                <span className="xs:hidden">Vorsch.</span>
              </Button>
              <Button
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 h-11 sm:h-10 text-sm sm:text-base"
                onClick={() => generatePDF(true, false)}
                disabled={loading}
              >
                <Download className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">PDF herunterladen</span>
                <span className="sm:hidden">Download</span>
              </Button>
              <Button
                className="bg-green-500 hover:bg-green-600 text-white h-11 sm:h-10 text-sm sm:text-base col-span-2 sm:col-span-1"
                onClick={() => generatePDF(true, true)}
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Speichern & Download</span>
                <span className="sm:hidden">Speichern</span>
              </Button>
              <Button
                className="bg-gray-50 hover:bg-gray-100 text-gray-500 border border-gray-200 h-11 sm:h-10 text-sm sm:text-base col-span-2 sm:col-span-1"
                onClick={resetForm}
              >
                <X className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Zurücksetzen</span>
                <span className="sm:hidden">Reset</span>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* Archiv Tab */
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="mb-4">
            <Button 
              onClick={() => setActiveTab('neu')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
            >
              <ChevronUp className="w-4 h-4 mr-2 rotate-[-90deg]" />
              Zurück zum Formular
            </Button>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {savedGutachten.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Noch keine Gutachten gespeichert</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-gray-600 text-sm font-medium">Nr.</th>
                      <th className="px-4 py-3 text-left text-gray-600 text-sm font-medium hidden sm:table-cell">Datum</th>
                      <th className="px-4 py-3 text-left text-gray-600 text-sm font-medium">Objekt</th>
                      <th className="px-4 py-3 text-left text-gray-600 text-sm font-medium hidden md:table-cell">Auftraggeber</th>
                      <th className="px-4 py-3 text-right text-gray-600 text-sm font-medium">Summe</th>
                      <th className="px-4 py-3 text-right text-gray-600 text-sm font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {savedGutachten.map(g => (
                      <tr key={g.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900 font-medium text-sm">{g.gutachtenNumber}</td>
                        <td className="px-4 py-3 text-gray-600 text-sm hidden sm:table-cell">
                          {new Date(g.inspectionDate).toLocaleDateString('de-DE')}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-sm">
                          <div className="truncate max-w-[150px] sm:max-w-none">
                            {g.objectStreet}
                          </div>
                          <div className="text-gray-400 text-xs sm:hidden">
                            {new Date(g.inspectionDate).toLocaleDateString('de-DE')}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-sm hidden md:table-cell">{g.clientName || '-'}</td>
                        <td className="px-4 py-3 text-[#9B1B30] font-medium text-sm text-right">
                          {(g.totalCost || 0).toFixed(2)} €
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => loadGutachten(g.id)}
                              className="text-gray-400 hover:text-[#9B1B30] hover:bg-red-50"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => downloadSavedGutachten(g.id)}
                              className="text-gray-400 hover:text-[#9B1B30] hover:bg-red-50"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl h-[90vh] p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Gutachten Vorschau</DialogTitle>
          </DialogHeader>
          {previewPdf && (
            <iframe
              src={previewPdf}
              className="w-full flex-1 rounded-b"
              title="PDF Preview"
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Image Description Dialog for AI Analysis */}
      <Dialog open={showImageDescDialog} onOpenChange={setShowImageDescDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              KI-Schadenanalyse
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Image Preview */}
            {pendingImageFile && (
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <img 
                  src={pendingImageFile} 
                  alt="Zu analysierendes Bild" 
                  className="w-full h-48 object-cover"
                />
              </div>
            )}
            
            {/* Description Input */}
            <div>
              <Label className="text-gray-700">
                Beschreibung hinzufügen (optional)
              </Label>
              <p className="text-sm text-gray-500 mb-2">
                Geben Sie der KI zusätzlichen Kontext, z.B. wo der Schaden ist, was passiert ist
              </p>
              <Textarea
                value={imageDescription}
                onChange={(e) => setImageDescription(e.target.value)}
                placeholder="z.B. Decke im Badezimmer, Wasser kommt von oben durch, Nachbar hatte Wasserschaden..."
                className="min-h-[100px]"
              />
            </div>
            
            {/* Examples */}
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-sm font-medium text-purple-700 mb-2">Beispiele für gute Beschreibungen:</p>
              <ul className="text-sm text-purple-600 space-y-1">
                <li>• "Schimmel hinter dem Schrank im Schlafzimmer, ca. 2m²"</li>
                <li>• "Wasserschaden an der Decke im Bad, Rohrbruch vom Nachbarn"</li>
                <li>• "Frostschaden an der Heizungsleitung im Keller"</li>
              </ul>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowImageDescDialog(false);
                  setPendingImageFile(null);
                }}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button 
                onClick={confirmImageAnalysis}
                disabled={aiLoading.imageAnalysis}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                {aiLoading.imageAnalysis ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analysiere...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Jetzt analysieren
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGutachten;
